import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { space } from 'styled-system';

import { Flex } from 'common/components/Flex';
import type { CommonTypographyProps } from 'common/components/Typography';
import { BodyRegularBR2, HeadlineH8, SubtitleST3 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import type { ListStyleProps } from 'common/theme/system-utilities';
import { listStyle } from 'common/theme/system-utilities';
import { localizeDate } from 'common/utils/date';
import type { ContactFormOrder } from '../../types';

type OrderLabelProps = ContactFormOrder & { orderHeadline: string };

const Items = styled(BodyRegularBR2)<CommonTypographyProps & ListStyleProps>`
  ${listStyle};
`;
const itemsStyles: ComponentProps<typeof Items> = {
  as: 'ul',
  m: 'sp_0',
  p: 'sp_0',
  listStyle: 'none',
};

const ListItem = styled.li<SpaceProps>`
  ${space};
`;

const OrderLabel = ({ grandTotal, createdAt, items, oms, orderHeadline }: OrderLabelProps) => {
  const { fc, t, locale } = useLocalization();
  const shopName = oms?.shop?.name ?? null;
  const orderDate = localizeDate(createdAt, locale);
  const itemDescriptions = items
    ?.map(({ article }) => {
      if (!article?.name) {
        return null;
      }

      if (!article?.variantName) {
        return article.name;
      }

      return `${article.name} - ${article.variantName}`;
    })
    ?.filter(v => v);

  return (
    <Flex flexDirection="column">
      <HeadlineH8 mb="sp_4">{orderHeadline}</HeadlineH8>
      <SubtitleST3 mb="sp_8">
        {fc(grandTotal)}, {orderDate}
      </SubtitleST3>
      {!!itemDescriptions?.length && (
        <Items {...itemsStyles}>
          {itemDescriptions.map((description, index) => (
            <li key={index}>{description}</li>
          ))}
          {shopName && (
            <ListItem mt="sp_8">{`${t('h24_contact_form_order_drawer_seller_info')}: ${shopName}`}</ListItem>
          )}
        </Items>
      )}
    </Flex>
  );
};

export default OrderLabel;
